/**
 * New-UX "Timeline Item Details" right-hand panel (list-icon section)
 * Reuses the existing skip/unskip/resend/reschedule/subtask service functions — this
 * file only owns new presentational JSX, not new business logic.
 *
 * NOTES ON NEW MULTI-SELECT ACTIONS
 * - "Retry" for an errored Task/Message re-sends on all channels;
 *    for Automations it force-runs that automation's ERROR actions.
 * - "Uncomplete" for a Done task reopens all of that task's DONE subtasks
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationActionStatus,
  EventChannel,
  ExperienceEventType,
  IExperience,
  IExperienceTemplate,
  MessageStatus,
  RequirementType,
  SubTaskStatus,
  SubTaskType
} from '@dadohr/shared/domainTypes/experience';
import { ScheduleChannel } from '@dadohr/shared/domainTypes/schedules';
import { MenuItem, TextField } from '@material-ui/core';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { isEqual, uniqBy } from 'lodash';
import shortid from 'shortid';
import { ArrowLeft, ArrowRight, CheckCircle2, ExternalLink, List, Mail, Play, Redo, RefreshCw, Send, Undo, X, Zap } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useCurrentUser } from '../../../../../services/currentUser';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../../i18next';
import { useRoutes } from '../../../../../routes';
import { updateDoc } from '../../../../../services/firebase';
import { LightMdToJsx } from '../../../../../services/formatter';
import { ItemData } from '../../../../../services/participatDialog/participantDialogHelpers';
import {
  forceRunAutomationActions,
  groupConcreteMessages,
  moveAutomationToToday,
  moveMessageToToday,
  moveSelectedExperienceItemsRelatively,
  moveTaskToToday,
  openSubTask,
  resendExperienceItem,
  skipAutomation,
  skipAutomationAction,
  skipMessage,
  skipSubTask,
  skipSubTaskAndTrigger,
  skipTask,
  unskipAutomation,
  unskipAutomationAction,
  unskipMessage,
  unskipTask
} from '../../../../../services/experiences/instance';
import { getTriggeredItemsForTask } from '../../../../../services/experiences/template';
import { SentItemErrorData } from '../InstanceProblems';
import { getActionErrorDetailsText, getSentItemErrorDetailsText } from '../ParticipantDialog';
import { ForceTriggerButton } from './ForceTriggerButton';
import { SkipLastSubTaskButton } from './SkipLastSubtaskButton';
import { RestartSignatureProcess } from './Task';
import { now, ONE_DAY, toMoment } from '../../../../../services/time';
import { CF } from '@dadohr/shared/versions';
import { callFirebaseFunction } from '../../../../../services/firebaseFunctions';
import { DateTimeHoverTooltip } from '../../../../../components/DateTimeTooltip';
import { SubTaskAsText } from '../../../../../components/SubTask';
import { Duration } from '../../../components/Builders/TaskBuilder/components/TimeSelectors';
import { ACTOR_COLORS_LIGHT } from '../../../components/Actors';
import { ChannelIcon } from '../../../components/Channels';
import { channelToKey, channelToString } from '../../../../../services/channels';
import { NewUxButton } from '../../../components/Builders/newux/kit/Button';
import { NewUxButtonWithPromise } from '../../../components/Builders/newux/kit/ButtonWithPromise';
import {
  ActorChip,
  StatusChip
} from '../../../components/Builders/newux/kit/controls';
import { tokens } from '../../../components/Builders/newux/kit/tokens';
import { getItemDisplayName, getItemDisplayPair } from '../../../helpers/adminName';
import { ActionWithIcon } from '../ParticipantDialog';
import { Selections } from '../TimelineItemStatusList';
import { SaveDiscardBar } from './SaveDiscardBar';
import {
  canResendItem,
  canSkipItem,
  canUncompleteItem,
  canUnskipItem,
  getAutomationActionDisplayStatus,
  getItemActualDate,
  getItemDate,
  getItemErrorConcreteMessage,
  getItemLocalRetryKey,
  getItemRetryCooldownMs,
  getItemScheduledDate,
  getItemStatusCategory,
  getItemStatusChipConfig,
  getMessageErrorState,
  getMultiSelectActionEligibility,
  getSkippableItems,
  getStartTodayEligibility,
  getSubTaskDisplayStatus,
  getTaskErrorState,
  getWriteFailedSubtaskTplIds,
  getTriggerTimelineItemUrl,
  getVisibleAudienceName,
  hasDocusignSubtask,
  getTriggerDescription,
  isErrorItem,
  isItemLate
} from './statusHelpers';
import {
  readLocalRetryMarks,
  recordLocalRetry
} from '../../../../../services/experiences/retryCooldown';

const { color, font, radius } = tokens;

const ITEM_TYPE_ICON: Record<ItemData['type'], React.ReactNode> = {
  MESSAGE: <Mail />,
  TASK: <CheckCircle2 />,
  AUTOMATION: <Zap />
};

/**
 * align-self: flex-start overrides the row's default stretch — without it, Panel would
 * match Container's full height (however long the table is), pinning its own max-height
 * (and therefore PanelScroll's internal scroll range) to that same, potentially huge,
 * height instead of the viewport. position: sticky + top: 0 then keeps it pinned near the
 * top of the viewport as you scroll through a long table, so selecting an item near the
 * bottom doesn't leave its controls scrolled off-screen above. max-height caps it to what
 * actually fits on screen once stuck — PanelScroll's own overflow-y: auto handles content
 * taller than that, rather than the lower portion becoming unreachable while pinned.
 */
const Panel = styled('div')`
  width: 500px;
  flex: 0 0 500px;
  align-self: flex-start;
  position: sticky;
  top: 0;
  max-height: calc(100vh - ${tokens.layout.topBarHeight}px);
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${color.borderSubtle};
`;

const PanelScroll = styled('div')`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: ${tokens.space(5)};
`;

const PanelHeaderRow = styled('div')`
  display: flex;
  align-items: center;
  gap: ${tokens.space(2)};
  margin-bottom: ${tokens.space(3)};
`;

const CloseItemButton = styled('button')`
  margin-left: auto;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: none;
  cursor: pointer;
  padding: 2px;
  color: ${color.n500};
  &:hover {
    color: ${color.textPrimary};
  }
`;

const HeaderDivider = styled('div')`
  border-top: 1px solid ${(p) => p.theme.custom.colors.gray.w300};
  margin: ${tokens.space(4)} 0;
`;

const PanelTitle = styled('div')`
  font-size: ${font.variant.h5.fontSize}px;
  line-height: ${font.variant.h5.lineHeight}px;
  font-weight: ${font.variant.h5.fontWeight};
`;

const PanelSubtitle = styled('div')`
  font-size: ${font.variant.body2.fontSize}px;
  line-height: ${font.variant.body2.lineHeight}px;
  font-weight: ${font.weightScale.regular};
  font-style: italic;
  color: ${color.textSecondary};
  margin-bottom: ${tokens.space(3)};
`;

const DateLine = styled('div')`
  font-size: ${font.variant.caption.fontSize}px;
  color: ${(p) => p.theme.custom.colors.gray.w700};
  margin-bottom: 2px;
  & strong {
    color: ${(p) => p.theme.custom.colors.gray.w700};
    font-weight: ${font.weightScale.medium};
  }
`;

const TriggerLine = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${font.variant.caption.fontSize}px;
  color: ${(p) => p.theme.custom.colors.gray.w700};
  margin-bottom: 2px;
  & strong {
    color: ${(p) => p.theme.custom.colors.gray.w700};
    font-weight: ${font.weightScale.medium};
  }
`;

const TemplateLink = styled('a')`
  display: inline-flex;
  align-items: center;
  color: ${color.blue500};
  & svg {
    width: 13px;
    height: 13px;
  }
`;

const SectionLabel = styled('div')`
  font-size: ${font.variant.h7.fontSize}px;
  line-height: ${font.variant.h7.lineHeight}px;
  font-weight: ${font.variant.h7.fontWeight};
  text-transform: ${font.variant.h7.textTransform};
  color: ${color.textSecondary};
  letter-spacing: 0.04em;
  margin: ${tokens.space(5)} 0 ${tokens.space(2)};
`;

const ButtonRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: ${tokens.space(2)};
`;

const SecondaryButton: React.FC<
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>
> = (
  props
) => <NewUxButton variant="outlined" color="neutral" size="small" {...props} />;

const SecondaryButtonWithPromise: React.FC<
  React.ComponentProps<typeof NewUxButtonWithPromise>
> = (props) => (
  <NewUxButtonWithPromise variant="outlined" color="neutral" size="small" {...props} />
);

const Hint = styled('div')`
  font-size: ${font.variant.caption.fontSize}px;
  color: ${color.textSecondary};
  background: ${(p) => p.theme.custom.colors.gray.w100};
  border-radius: ${radius.md}px;
  padding: ${tokens.space(2)} ${tokens.space(3)};
  margin-top: ${tokens.space(2)};
`;

const ErrorBanner = styled('div')`
  font-size: ${font.variant.body2.fontSize}px;
  color: ${(p) => p.theme.custom.colors.red.w600};
  background: ${(p) => p.theme.custom.colors.red.w100};
  border-radius: ${radius.md}px;
  padding: ${tokens.space(3)};
  margin-top: ${tokens.space(2)};
  margin-bottom: ${tokens.space(2)};
`;

const SubtaskRow = styled('div')`
  display: flex;
  align-items: center;
  gap: ${tokens.space(2)};
  padding: ${tokens.space(2)} 0;
  border-bottom: 1px solid ${color.borderSubtle};
  &:last-child {
    border-bottom: 0;
  }
`;

const DeselectItemButton = styled('button')`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: none;
  cursor: pointer;
  padding: 2px;
  color: ${color.n500};
  &:hover {
    color: ${color.textPrimary};
  }
`;

const SubtaskLabel = styled('div')`
  flex: 1 1 auto;
  font-size: ${font.variant.caption.fontSize}px;
  color: ${color.textPrimary};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RescheduleRow = styled('div')`
  display: flex;
  align-items: center;
  gap: ${tokens.space(2)};
`;

const RescheduleLabel = styled('span')`
  font-size: ${font.variant.body2.fontSize}px;
  color: ${color.textPrimary};
`;

const Placeholder = styled('div')`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  color: ${color.textSecondary};
  font-size: ${font.variant.body2.fontSize}px;
  line-height: ${font.variant.body2.lineHeight}px;
  padding: 60px ${tokens.space(5)} ${tokens.space(5)};
  & svg {
    width: 24px;
    height: 24px;
    color: ${color.n400};
    margin-bottom: ${tokens.space(3)};
  }
`;

const itemKey = (item: ItemData) => `${item.type}-${item.data.templateId}`;

const buildSingleItemSelections = (item: ItemData): Selections => ({
  tasks: item.type === 'TASK' ? { [item.data.templateId]: true } : {},
  messages: item.type === 'MESSAGE' ? { [item.data.templateId]: true } : {},
  automations: item.type === 'AUTOMATION' ? { [item.data.templateId]: true } : {}
});

const RescheduleControl: React.FC<{
  onApply: (msToMove: number) => void;
}> = ({ onApply }) => {
  const { t } = useUnsafeTranslation();
  const [amount, setAmount] = useState(ONE_DAY);
  const [direction, setDirection] = useState<'later' | 'earlier'>('later');

  return (
    <RescheduleRow>
      <RescheduleLabel>{t('Start')}</RescheduleLabel>
      <Duration value={amount} onChange={setAmount} />
      <TextField
        select
        variant="outlined"
        size="small"
        value={direction}
        onChange={(e) => setDirection(e.target.value as 'later' | 'earlier')}
      >
        <MenuItem value="later">{t('Later')}</MenuItem>
        <MenuItem value="earlier">{t('Earlier')}</MenuItem>
      </TextField>
      <SecondaryButton
        type="button"
        style={{ marginLeft: tokens.space(2) }}
        onClick={() => onApply(direction === 'earlier' ? -amount : amount)}
      >
        {direction === 'earlier' ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}{' '}
        {t('Move')}
      </SecondaryButton>
    </RescheduleRow>
  );
};

/**
 * Directly writes a resent message's status to DONE, bypassing the panel's usual local
 * setD staging — matches classic (TimelineItemStatusList.tsx's onSuccessfulResend), which
 * clears a message's Error status immediately on a successful resend rather than waiting
 * for the next Save.
 */
const markMessageResent = async (d: Doc<IExperience>, msgTemplateId: string) => {
  d.data.messagesStatuses[msgTemplateId].status = MessageStatus.DONE;
  await updateDoc(d, (x) => ({ messagesStatuses: x.messagesStatuses }));
};

// SC-36146: shown after every successfully queued retry.
const RETRY_QUEUED_COPY = 'Retry in progress';

const ResendButtons: React.FC<{
  item: Extract<ItemData, { type: 'TASK' | 'MESSAGE' }>;
  d: Doc<IExperience>;
  orgId: string;
  experienceId: string;
}> = ({ item, d, orgId, experienceId }) => {
  const { t } = useUnsafeTranslation();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  const doResend = async (channels: ScheduleChannel[] | 'ALL') => {
    if (channels === 'ALL') channels = item.channels;
    if (!channels.length) return;
    const base = { orgId, experienceId, actor: item.actor.key, channels };
    try {
      if (item.type === 'TASK') {
        await resendExperienceItem({
          ...base,
          itemIds: [item.data.id],
          itemType: 'TASK'
        });
      } else {
        await resendExperienceItem({
          ...base,
          itemId: item.data.templateId,
          itemType: 'MESSAGE',
          includedTaskStartIds: []
        });
        if (item.data.status === MessageStatus.ERROR) {
          await markMessageResent(d, item.data.templateId);
        }
      }
      enqueueSnackbarSuccess(t('Item resent successfully!'));
    } catch {
      enqueueSnackbarError(t('Failed to resend item!'));
    }
  };

  const uniqueChannels = uniqBy(item.channels, channelToKey);

  return (
    <ButtonRow>
      {uniqueChannels.map((c) => (
        <SecondaryButton key={channelToKey(c)} type="button" onClick={() => doResend([c])}>
          <ChannelIcon channel={c} size={16} /> {channelToString(c, t)}
        </SecondaryButton>
      ))}
      {item.channels.length > 0 && (
        <SecondaryButton type="button" onClick={() => doResend('ALL')}>
          <Send size={16} /> {t('All channels')}
        </SecondaryButton>
      )}
    </ButtonRow>
  );
};

const SingleItemDetailsPanel: React.FC<{
  tpl: Doc<IExperienceTemplate>;
  d: Doc<IExperience>;
  setD: React.Dispatch<React.SetStateAction<Doc<IExperience>>>;
  originalD: Doc<IExperience>;
  item: ItemData;
  setSelections: React.Dispatch<React.SetStateAction<Selections>>;
  saveInstance: (toSave: Doc<IExperience>) => Promise<void>;
}> = ({ tpl, d, setD, originalD, item, setSelections, saveInstance }) => {
  const { t } = useUnsafeTranslation();
  const { ROUTES } = useRoutes();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  const { id: currentUserId, org } = useCurrentUser();
  const theme = useTheme();
  const actorLabel = item.type === 'AUTOMATION' ? t('Automation') : item.actor.label;
  const actorColor =
    item.type === 'AUTOMATION' ? theme.custom.colors.purple.w300 : item.actor.color;
  const isLegacyActorColor =
    item.type !== 'AUTOMATION' &&
    !ACTOR_COLORS_LIGHT.some((c) => c.hex.toLowerCase() === actorColor.toLowerCase());
  const concreteMessages = useMemo(
    () => groupConcreteMessages(d.data.messages),
    [d.data.messages]
  );
  const writeFailedSubtaskTplIds = useMemo(
    () => getWriteFailedSubtaskTplIds(d.data.instanceErrors),
    [d.data.instanceErrors]
  );
  const taskErrorState = useMemo(
    () =>
      item.type === 'TASK'
        ? getTaskErrorState(item.data, concreteMessages.byTask, undefined, writeFailedSubtaskTplIds)
        : null,
    [item, concreteMessages, writeFailedSubtaskTplIds]
  );
  const messageErrorState = useMemo(
    () =>
      item.type === 'MESSAGE'
        ? getMessageErrorState(item.data.templateId, item.data.status, concreteMessages.byMessage)
        : null,
    [item, concreteMessages]
  );
  // SC-36146: "retry queued — wait a few minutes" state.
  const [nowMillis, setNowMillis] = useState(() => Date.now());
  const [localRetryMarks, setLocalRetryMarks] = useState(() =>
    readLocalRetryMarks(Date.now())
  );
  const [retrying, setRetrying] = useState(false);
  useEffect(() => {
    const id = setInterval(() => setNowMillis(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  const retryCooldownMs = useMemo(
    () =>
      getItemRetryCooldownMs(
        item,
        d,
        getItemErrorConcreteMessage(item, concreteMessages.byTask, concreteMessages.byMessage),
        nowMillis,
        localRetryMarks
      ),
    [item, d, concreteMessages, nowMillis, localRetryMarks]
  );
  const [label, chipStyle] = getItemStatusChipConfig(item, t, taskErrorState, messageErrorState);
  const category = getItemStatusCategory(item, taskErrorState, messageErrorState);
  const itemDisplayPair = getItemDisplayPair(item.data);
  const date = getItemDate(item);
  const actualDate = getItemActualDate(item, concreteMessages.byTask, concreteMessages.byMessage);
  const late = isItemLate(getItemScheduledDate(item), actualDate);
  const trigger = getTriggerDescription(item, t);
  const visibleAudienceName = getVisibleAudienceName(item);
  const startTodayEligibility = getStartTodayEligibility(item, category);

  // Item-level error banner: for a Task/Message, the SENT_ITEM issue's one-line
  // summary (`newCopy`, via getSentItemErrorDetailsText) for the concrete message
  // its Error status was derived from; for an Automation, a pointer to the
  // per-action error boxes in the Actions section below, since the automation's
  // own error is really one or more of its actions failing.
  const errorBannerText = useMemo(() => {
    if (!isErrorItem(item, taskErrorState, messageErrorState)) return null;
    if (item.type === 'AUTOMATION') {
      return item.data.actions.some((a) => a.status === AutomationActionStatus.ERROR)
        ? t('See error details below')
        : null;
    }
    if (taskErrorState === 'WRITE_FAILED') {
      return t("An answer in this task couldn't be saved to a data destination. See Issues for details and Retry.");
    }
    const errorMessage = getItemErrorConcreteMessage(
      item,
      concreteMessages.byTask,
      concreteMessages.byMessage
    );
    if (!errorMessage) return null;
    const sentItem: SentItemErrorData = {
      type: 'SENT_ITEM',
      tpl,
      instance: d,
      id: errorMessage.msgId,
      itemTplId: item.data.templateId,
      includedItems: [],
      humanReadableTitle: itemDisplayPair.primary,
      actor: item.actor.key,
      message: errorMessage,
      channels: item.channels
    };
    return getSentItemErrorDetailsText({
      sentItem,
      supportingActors: tpl.data.supportingActors,
      orgFeatures: org.features,
      t
    });
  }, [item, taskErrorState, messageErrorState, concreteMessages, tpl, d, itemDisplayPair, org.features, t]);

  const doStartToday = () => {
    if (item.type === 'TASK') setD(moveTaskToToday(tpl, d, item.data.id));
    else if (item.type === 'MESSAGE') setD(moveMessageToToday(tpl, d, item.data.templateId));
    else setD(moveAutomationToToday(tpl, d, item.data.templateId));
  };

  const doSkip = () => {
    if (item.type === 'TASK') setD(skipTask(tpl, d, item.data.id));
    else if (item.type === 'MESSAGE') setD(skipMessage(d, item.data.templateId));
    else setD(skipAutomation(d, item.data.templateId));
  };
  const doUnskip = () => {
    if (item.type === 'TASK') setD(unskipTask(tpl, d, item.data.id));
    else if (item.type === 'MESSAGE') setD(unskipMessage(d, item.data.templateId));
    else setD(unskipAutomation(d, item.data.templateId));
  };
  const doUncomplete = () => {
    if (item.type !== 'TASK') return;
    let nextD = d;
    item.data.subTasks
      .filter((s) => s.status === SubTaskStatus.DONE && s.type !== SubTaskType.DOCUSIGN_ENVELOPE_SIGN)
      .forEach((s) => {
        nextD = openSubTask(tpl, nextD, item.data.id, s.id);
      });
    setD(nextD);
  };
  // Same cooldown as the item-level Retry: the mark is per automation, so retrying one
  // action holds off every Retry on this automation until the outcome is known.
  const doRetryAction = async (actionTplId: string) => {
    if (item.type !== 'AUTOMATION') return;
    setRetrying(true);
    try {
      await forceRunAutomationActions(
        d.data.orgId,
        [
          {
            experienceTplId: tpl.id,
            automationTplId: item.data.templateId,
            actionTplId,
            instanceId: d.id
          }
        ],
        Date.now()
      );
      const marks = recordLocalRetry(
        getItemLocalRetryKey(d.data.orgId, d.id, item),
        Date.now()
      );
      setLocalRetryMarks((prev) => ({ ...prev, ...marks }));
      setNowMillis(Date.now());
      enqueueSnackbarSuccess(t(RETRY_QUEUED_COPY));
    } catch {
      enqueueSnackbarError(t('Could not retry this action'));
    } finally {
      setRetrying(false);
    }
  };
  const doRetry = async () => {
    setRetrying(true);
    try {
      if (item.type === 'AUTOMATION') {
        const errored = item.data.actions.filter(
          (a) => a.status === AutomationActionStatus.ERROR
        );
        await forceRunAutomationActions(
          d.data.orgId,
          errored.map((a) => ({
            experienceTplId: tpl.id,
            automationTplId: item.data.templateId,
            actionTplId: a.templateId,
            instanceId: d.id
          })),
          Date.now()
        );
        // Force-runs record no BE event — remember the retry in this browser.
        const marks = recordLocalRetry(
          getItemLocalRetryKey(d.data.orgId, d.id, item),
          Date.now()
        );
        setLocalRetryMarks((prev) => ({ ...prev, ...marks }));
      } else if (item.type === 'MESSAGE') {
        // Resends on all channels — same underlying resendExperienceItem call and
        // error-clearing behavior as the "All channels" Resend button.
        await resendExperienceItem({
          orgId: d.data.orgId,
          experienceId: d.id,
          itemId: item.data.templateId,
          itemType: 'MESSAGE',
          actor: item.actor.key,
          channels: item.channels,
          includedTaskStartIds: []
        });
        if (item.data.status === MessageStatus.ERROR) {
          await markMessageResent(d, item.data.templateId);
        }
      }
      setNowMillis(Date.now());
      enqueueSnackbarSuccess(t(RETRY_QUEUED_COPY));
    } catch {
      enqueueSnackbarError(t('Could not retry this item'));
    } finally {
      setRetrying(false);
    }
  };
  const doReschedule = (msToMove: number) => {
    setD((x) =>
      moveSelectedExperienceItemsRelatively(
        tpl,
        x,
        buildSingleItemSelections(item),
        msToMove,
        now()
      )
    );
  };

  // Matches classic's onRestartDocusignSubTask (ParticipantDialog.tsx) exactly: reopens
  // every affected subtask, records a DOCUSIGN_RESTARTED event, then saves immediately
  // (bypassing this panel's usual local-staging Save/Discard flow) alongside the actual
  // Docusign void-and-recreate call.
  const doRestartDocusign = async (
    trigger: { taskTplId: string; subTaskTplId: string; resourceId: string },
    items: { taskTplId: string; subTaskTplId: string; resourceId: string }[],
    voidedReason: string | null
  ) => {
    const nextD = items.reduce((nd, x) => {
      const tsk = d.data.tasks.find((tk) => tk.templateId === x.taskTplId);
      if (tsk) {
        const subTask = tsk.subTasks.find((s) => s.templateId === x.subTaskTplId);
        if (subTask) return openSubTask(tpl, nd, tsk.id, subTask.id);
      }
      return nd;
    }, d);

    nextD.data.events.push({
      id: shortid(),
      type: ExperienceEventType.DOCUSIGN_RESTARTED,
      resourceId: trigger.resourceId,
      at: now(),
      channel: EventChannel.ADMIN_APP,
      initiator: currentUserId
    });

    setD(nextD);
    await Promise.all([
      saveInstance(nextD),
      callFirebaseFunction('integrations_docusign-voidExistingAndMaybeCreateNewEnvelope', {
        orgId: tpl.data.orgId,
        tplId: tpl.id,
        expId: nextD.id,
        subTaskTemplateId: trigger.subTaskTplId,
        taskTemplateId: trigger.taskTplId,
        resourceId: trigger.resourceId,
        voidedReason,
        createNewEnvelope: true
      })
    ]);
  };

  return (
    <PanelScroll>
      <PanelHeaderRow>
        <StatusChip tone={chipStyle.tone} variant={chipStyle.variant}>
          {label}
        </StatusChip>
        <ActorChip
          color={actorColor}
          icon={ITEM_TYPE_ICON[item.type]}
          dimmed={isLegacyActorColor}
        >
          {actorLabel}
        </ActorChip>
        <CloseItemButton
          type="button"
          aria-label={t('Deselect')}
          onClick={() => setSelections({ tasks: {}, messages: {}, automations: {} })}
        >
          <X size={16} />
        </CloseItemButton>
      </PanelHeaderRow>
      <PanelTitle style={itemDisplayPair.secondary ? { marginBottom: tokens.space(1) } : { marginBottom: tokens.space(3) }}>
        {itemDisplayPair.primary}
      </PanelTitle>
      {itemDisplayPair.secondary && <PanelSubtitle>{itemDisplayPair.secondary}</PanelSubtitle>}

      {item.type === 'TASK' && (
        <>
          {trigger ? (
            <TriggerLine>
              <span>
                <strong>{trigger.verb}</strong>: {trigger.parentNames}
              </span>
              <TemplateLink
                href={getTriggerTimelineItemUrl(ROUTES, tpl.id, item) ?? undefined}
                target="_blank"
                rel="noopener"
              >
                <ExternalLink size={16} />
              </TemplateLink>
            </TriggerLine>
          ) : category === 'UPCOMING' ? (
            <DateLine>
              <strong>{t('Starts')}</strong>{' '}
              <DateTimeHoverTooltip at={item.data.start}>
                {toMoment(item.data.start).tz(tpl.data.tz).format('ddd, D MMM YYYY, h:mm A z')}
              </DateTimeHoverTooltip>
            </DateLine>
          ) : category === 'SKIPPED' ? (
            <DateLine>
              <strong>{t('Would have started')}</strong>{' '}
              <DateTimeHoverTooltip at={item.data.start}>
                {toMoment(item.data.start).tz(tpl.data.tz).format('ddd, D MMM YYYY, h:mm A z')}
              </DateTimeHoverTooltip>
            </DateLine>
          ) : late && actualDate ? (
            <>
              <DateLine>
                <strong>{t('Should have started')}</strong>{' '}
                <DateTimeHoverTooltip at={item.data.start}>
                  {toMoment(item.data.start).tz(tpl.data.tz).format('ddd, D MMM YYYY, h:mm A z')}
                </DateTimeHoverTooltip>
              </DateLine>
              <DateLine>
                <strong>{t('Started')}</strong>{' '}
                <DateTimeHoverTooltip at={actualDate}>
                  {toMoment(actualDate).tz(tpl.data.tz).format('ddd, D MMM YYYY, h:mm A z')}
                </DateTimeHoverTooltip>
              </DateLine>
            </>
          ) : (
            <DateLine>
              <strong>{t('Started')}</strong>{' '}
              <DateTimeHoverTooltip at={actualDate || item.data.start}>
                {toMoment(actualDate || item.data.start).tz(tpl.data.tz).format('ddd, D MMM YYYY, h:mm A z')}
              </DateTimeHoverTooltip>
            </DateLine>
          )}
          <DateLine>
            <strong>{t('Due')}</strong>{' '}
            <DateTimeHoverTooltip at={item.data.end}>
              {toMoment(item.data.end).tz(tpl.data.tz).format('ddd, D MMM YYYY, h:mm A z')}
            </DateTimeHoverTooltip>
          </DateLine>
          {item.data.completedAt && (
            <DateLine>
              <strong>{t('Completed')}</strong>{' '}
              <DateTimeHoverTooltip at={item.data.completedAt}>
                {toMoment(item.data.completedAt).tz(tpl.data.tz).format('ddd, D MMM YYYY, h:mm A z')}
              </DateTimeHoverTooltip>
            </DateLine>
          )}
        </>
      )}
      {item.type !== 'TASK' && (
        trigger ? (
          <TriggerLine>
            <span>
              <strong>{trigger.verb}</strong>: {trigger.parentNames}
            </span>
            <TemplateLink
              href={getTriggerTimelineItemUrl(ROUTES, tpl.id, item) ?? undefined}
              target="_blank"
              rel="noopener"
            >
              <ExternalLink size={16} />
            </TemplateLink>
          </TriggerLine>
        ) : date ? (
          <>
            <DateLine>
              <strong>
                {item.type === 'MESSAGE'
                  ? category === 'UPCOMING'
                    ? t('Sends')
                    : category === 'SKIPPED'
                    ? t('Would have sent')
                    : category === 'ERROR' || (late && actualDate)
                    ? t('Should have sent')
                    : t('Sent')
                  : category === 'UPCOMING'
                  ? t('Runs')
                  : category === 'SKIPPED'
                  ? t('Would have run')
                  : category === 'ERROR' || (late && actualDate)
                  ? t('Should have run')
                  : t('Ran')}
              </strong>{' '}
              <DateTimeHoverTooltip at={date}>
                {toMoment(date).tz(tpl.data.tz).format('ddd, D MMM YYYY, h:mm A z')}
              </DateTimeHoverTooltip>
            </DateLine>
            {late && actualDate && category !== 'UPCOMING' && category !== 'SKIPPED' && (
              <DateLine>
                <strong>{item.type === 'MESSAGE' ? t('Sent') : t('Ran')}</strong>{' '}
                <DateTimeHoverTooltip at={actualDate}>
                  {toMoment(actualDate).tz(tpl.data.tz).format('ddd, D MMM YYYY, h:mm A z')}
                </DateTimeHoverTooltip>
              </DateLine>
            )}
          </>
        ) : null
      )}

      {visibleAudienceName && (
        <DateLine style={{ marginTop: tokens.space(3) }}>
          <strong>{t('Audience')}</strong> {visibleAudienceName}
        </DateLine>
      )}

      <HeaderDivider />

      {errorBannerText && (
        <ErrorBanner>
          <LightMdToJsx>{errorBannerText}</LightMdToJsx>
        </ErrorBanner>
      )}

      {(item.type === 'TASK' && canUncompleteItem(item)) ||
      (item.type !== 'TASK' && isErrorItem(item, taskErrorState, messageErrorState)) ||
      canSkipItem(item, taskErrorState, messageErrorState) ||
      canUnskipItem(item) ? (
        <>
          <SectionLabel>{t('Manage')}</SectionLabel>
          <ButtonRow>
            {canUncompleteItem(item) && item.type === 'TASK' && (
              <SecondaryButton type="button" onClick={doUncomplete}>
                <Undo size={16} /> {t('Uncomplete')}
              </SecondaryButton>
            )}
            {item.type !== 'TASK' && isErrorItem(item, taskErrorState, messageErrorState) && (
              <SecondaryButton
                type="button"
                onClick={doRetry}
                disabled={retrying || retryCooldownMs > 0}
              >
                <RefreshCw size={16} />{' '}
                {retrying
                  ? t('Retrying…')
                  : retryCooldownMs > 0
                    ? t('Retry in progress')
                    : t('Retry')}
              </SecondaryButton>
            )}
            {canSkipItem(item, taskErrorState, messageErrorState) && (
              <SecondaryButton type="button" onClick={doSkip}>
                <Redo size={16} /> {t('Skip')}
              </SecondaryButton>
            )}
            {canUnskipItem(item) && (
              <SecondaryButton type="button" onClick={doUnskip}>
                <Undo size={16} /> {t('Unskip')}
              </SecondaryButton>
            )}
          </ButtonRow>
          {item.type !== 'TASK' &&
            isErrorItem(item, taskErrorState, messageErrorState) &&
            retryCooldownMs > 0 && (
              <Hint>
                {t(
                  'Retries can take up to 2 minutes. If unsuccessful, the error will re-appear here automatically.'
                )}
              </Hint>
            )}
        </>
      ) : null}

      {canResendItem(item, taskErrorState) && item.type !== 'AUTOMATION' && (
        <>
          <SectionLabel>{t('Resend')}</SectionLabel>
          <ResendButtons
            item={item}
            d={d}
            orgId={d.data.orgId}
            experienceId={d.id}
          />
          {hasDocusignSubtask(item) && (
            <>
              <Hint>
                {t('Resending in Dado will not resend the Docusign notification. To do this, click')}{' '}
                <strong>{t('Restart signature process')}</strong> {t('instead.')}
              </Hint>
              <ButtonRow style={{ marginTop: tokens.space(2) }}>
                {item.type === 'TASK' &&
                  item.data.subTasks
                    .filter((s) => s.type === SubTaskType.DOCUSIGN_ENVELOPE_SIGN)
                    .map((s) => (
                      <RestartSignatureProcess
                        key={s.id}
                        tpl={tpl}
                        instanceId={item.data.experienceId}
                        taskTplId={item.data.templateId}
                        subTaskTplId={s.templateId}
                        onRestart={doRestartDocusign}
                      />
                    ))}
              </ButtonRow>
            </>
          )}
        </>
      )}

      <SectionLabel>{t('Reschedule')}</SectionLabel>
      {category !== 'DONE' && category !== 'SKIPPED' && (
        <>
          {startTodayEligibility === 'FIXED_DATE' && (
            <ButtonRow style={{ marginBottom: tokens.space(2) }}>
              <SecondaryButton type="button" onClick={doStartToday}>
                <Play size={16} />{' '}
                {item.type === 'TASK'
                  ? t('Start today')
                  : item.type === 'MESSAGE'
                  ? t('Send today')
                  : t('Run today')}
              </SecondaryButton>
            </ButtonRow>
          )}
          {startTodayEligibility === 'TRIGGERED' && (
            <ButtonRow style={{ marginBottom: tokens.space(2) }}>
              <ForceTriggerButton
                item={item.data}
                itemType={item.type === 'TASK' ? 'task' : item.type === 'MESSAGE' ? 'message' : 'automation'}
                triggeredBy={item.triggeredBy || []}
                isUntriggered
                template={tpl.data}
                onForceTrigger={() =>
                  callFirebaseFunction(CF.experiences.forceTriggerItem, {
                    orgId: d.data.orgId,
                    tplId: tpl.id,
                    instanceId: d.id,
                    itemType: item.type === 'MESSAGE' ? 'MSG' : item.type,
                    itemTemplateId: item.data.templateId
                  })
                }
                enqueueSnackbarSuccess={enqueueSnackbarSuccess}
                enqueueSnackbarError={enqueueSnackbarError}
                t={t}
              />
            </ButtonRow>
          )}
        </>
      )}
      <RescheduleControl onApply={doReschedule} />

      {item.type === 'TASK' && !!item.data.subTasks.length && (
        <>
          <SectionLabel>{t('Subtasks')}</SectionLabel>
          {item.data.subTasks.map((s) => {
            const [subLabel, subStyle] = getSubTaskDisplayStatus(
              s.status,
              category,
              t
            );
            return (
              <SubtaskRow key={s.id}>
                <SubtaskLabel><SubTaskAsText s={s} /></SubtaskLabel>
                <StatusChip tone={subStyle.tone} variant={subStyle.variant}>
                  {subLabel}
                </StatusChip>
                {s.status === SubTaskStatus.OPEN && item.type === 'TASK' && (() => {
                  const openSubTasks = item.data.subTasks.filter(
                    (st) => st.status === SubTaskStatus.OPEN && st.requirementType === RequirementType.MANDATORY
                  );
                  const isLastSubTask = openSubTasks.length === 1 && openSubTasks[0].id === s.id;
                  const triggeredItems = getTriggeredItemsForTask(tpl, item.data.templateId);
                  const hasCompletedSubTasks = item.data.subTasks.some((st) => st.status === SubTaskStatus.DONE);
                  if (isLastSubTask && triggeredItems?.length && hasCompletedSubTasks) {
                    return (
                      <SkipLastSubTaskButton
                        subTask={s}
                        task={item.data}
                        template={tpl}
                        onSkipNoTrigger={(subTaskId) => setD(skipSubTask(tpl, d, item.data.id, subTaskId))}
                        onSkipSubTask={(subTaskId) =>
                          skipSubTaskAndTrigger(tpl, originalD, d, item.data.id, subTaskId)
                        }
                        enqueueSnackbarSuccess={enqueueSnackbarSuccess}
                        enqueueSnackbarError={enqueueSnackbarError}
                        t={t}
                      />
                    );
                  }
                  return (
                    <SecondaryButton
                      type="button"
                      onClick={() => setD(skipSubTask(tpl, d, item.data.id, s.id))}
                    >
                      <Redo size={16} /> {t('Skip')}
                    </SecondaryButton>
                  );
                })()}
                {s.status === SubTaskStatus.SKIPPED && (
                  <SecondaryButton
                    type="button"
                    onClick={() =>
                      setD(openSubTask(tpl, d, item.data.id, s.id))
                    }
                  >
                    <Undo size={16} /> {t('Unskip')}
                  </SecondaryButton>
                )}
                {s.status === SubTaskStatus.DONE && s.type !== SubTaskType.DOCUSIGN_ENVELOPE_SIGN && (
                  <SecondaryButton
                    type="button"
                    onClick={() =>
                      setD(openSubTask(tpl, d, item.data.id, s.id))
                    }
                  >
                    <Undo size={16} /> {t('Uncomplete')}
                  </SecondaryButton>
                )}
              </SubtaskRow>
            );
          })}
        </>
      )}

      {item.type === 'AUTOMATION' && !!item.data.actions.length && (
        <>
          <SectionLabel>{t('Actions')}</SectionLabel>
          {item.data.actions.map((a) => {
            const [actionLabel, actionStyle] = getAutomationActionDisplayStatus(
              a.status,
              t
            );
            const automationTplId = item.type === 'AUTOMATION' ? item.data.templateId : '';
            const actionErrorText =
              a.status === AutomationActionStatus.ERROR
                ? getActionErrorDetailsText({
                    action: a,
                    customUserFields: org.customUserFields,
                    supportingActors: tpl.data.supportingActors,
                    orgFeatures: org.features,
                    t
                  })
                : null;
            return (
              <React.Fragment key={a.id}>
                <SubtaskRow>
                  <SubtaskLabel><ActionWithIcon action={a} small wrap /></SubtaskLabel>
                  <StatusChip tone={actionStyle.tone} variant={actionStyle.variant}>
                    {actionLabel}
                  </StatusChip>
                  {(a.status === AutomationActionStatus.UPCOMING ||
                    a.status === AutomationActionStatus.ERROR) && (
                    <SecondaryButton
                      type="button"
                      onClick={() => setD(skipAutomationAction(d, automationTplId, a.templateId))}
                    >
                      <Redo size={16} /> {t('Skip')}
                    </SecondaryButton>
                  )}
                  {a.status === AutomationActionStatus.ERROR && (
                    <SecondaryButtonWithPromise
                      type="button"
                      pending={`${t('Retrying')}...`}
                      disabled={retrying || retryCooldownMs > 0}
                      onClick={() => doRetryAction(a.templateId)}
                    >
                      <RefreshCw size={16} />{' '}
                      {retryCooldownMs > 0 ? t('Retry in progress') : t('Retry')}
                    </SecondaryButtonWithPromise>
                  )}
                  {a.status === AutomationActionStatus.SKIPPED && (
                    <SecondaryButton
                      type="button"
                      onClick={() => setD(unskipAutomationAction(d, automationTplId, a.templateId))}
                    >
                      <Undo size={16} /> {t('Unskip')}
                    </SecondaryButton>
                  )}
                </SubtaskRow>
                {actionErrorText && (
                  <ErrorBanner>
                    <LightMdToJsx>{actionErrorText}</LightMdToJsx>
                  </ErrorBanner>
                )}
              </React.Fragment>
            );
          })}
        </>
      )}
    </PanelScroll>
  );
};

const MultiItemDetailsPanel: React.FC<{
  tpl: Doc<IExperienceTemplate>;
  d: Doc<IExperience>;
  setD: React.Dispatch<React.SetStateAction<Doc<IExperience>>>;
  items: ItemData[];
  onDeselectAll: () => void;
  onDeselectItem: (item: ItemData) => void;
}> = ({ tpl, d, setD, items, onDeselectAll, onDeselectItem }) => {
  const { t } = useUnsafeTranslation();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();

  const groupedConcreteMessages = useMemo(
    () => groupConcreteMessages(d.data.messages),
    [d.data.messages]
  );
  const concreteMessagesByTask = groupedConcreteMessages.byTask;
  const concreteMessagesByMessage = groupedConcreteMessages.byMessage;
  const writeFailedSubtaskTplIds = useMemo(
    () => getWriteFailedSubtaskTplIds(d.data.instanceErrors),
    [d.data.instanceErrors]
  );
  const taskErrorStates = useMemo(
    () =>
      items.map((i) =>
        i.type === 'TASK'
          ? getTaskErrorState(i.data, concreteMessagesByTask, undefined, writeFailedSubtaskTplIds)
          : null
      ),
    [items, concreteMessagesByTask, writeFailedSubtaskTplIds]
  );
  const messageErrorStates = useMemo(
    () =>
      items.map((i) =>
        i.type === 'MESSAGE'
          ? getMessageErrorState(i.data.templateId, i.data.status, concreteMessagesByMessage)
          : null
      ),
    [items, concreteMessagesByMessage]
  );
  const categories = useMemo(
    () =>
      items.map((i, idx) =>
        getItemStatusCategory(i, taskErrorStates[idx], messageErrorStates[idx])
      ),
    [items, taskErrorStates, messageErrorStates]
  );
  const {
    anyDone,
    anyError,
    anySkipped,
    anySkippable,
    resendableItems,
    anyUpcomingResendable
  } = getMultiSelectActionEligibility(items, categories);

  const startTodayEligibilities = useMemo(
    () => items.map((i, idx) => getStartTodayEligibility(i, categories[idx])),
    [items, categories]
  );
  const anyStartTodayEligible = startTodayEligibilities.some((e) => e !== null);

  const selections = useMemo<Selections>(() => {
    const s: Selections = { tasks: {}, messages: {}, automations: {} };
    items.forEach((i) => {
      if (i.type === 'TASK') s.tasks[i.data.templateId] = true;
      else if (i.type === 'MESSAGE') s.messages[i.data.templateId] = true;
      else s.automations[i.data.templateId] = true;
    });
    return s;
  }, [items]);

  const applyToAll = (fn: (item: ItemData, current: Doc<IExperience>) => Doc<IExperience>) => {
    let nextD = d;
    items.forEach((i) => {
      nextD = fn(i, nextD);
    });
    setD(nextD);
  };

  const doSkipAll = () => {
    let nextD = d;
    getSkippableItems(items, categories).forEach((i) => {
      if (i.type === 'TASK') nextD = skipTask(tpl, nextD, i.data.id);
      else if (i.type === 'MESSAGE') nextD = skipMessage(nextD, i.data.templateId);
      else nextD = skipAutomation(nextD, i.data.templateId);
    });
    setD(nextD);
  };

  const doUnskipAll = () =>
    applyToAll((i, cur) => {
      if (i.type === 'TASK') return unskipTask(tpl, cur, i.data.id);
      if (i.type === 'MESSAGE') return unskipMessage(cur, i.data.templateId);
      return unskipAutomation(cur, i.data.templateId);
    });

  const doUncompleteAll = () => {
    let nextD = d;
    items
      .filter((i) => i.type === 'TASK')
      .forEach((i) => {
        if (i.type !== 'TASK') return;
        i.data.subTasks
          .filter((s) => s.status === SubTaskStatus.DONE && s.type !== SubTaskType.DOCUSIGN_ENVELOPE_SIGN)
          .forEach((s) => {
            nextD = openSubTask(tpl, nextD, i.data.id, s.id);
          });
      });
    setD(nextD);
  };

  const doRetryAllErrors = async () => {
    // Tasks have no Retry action (classic offers only Resend for an errored task).
    const errored = items.filter((i, idx) => i.type !== 'TASK' && categories[idx] === 'ERROR');
    const automationRetryArgs = errored
      .filter((i): i is Extract<ItemData, { type: 'AUTOMATION' }> => i.type === 'AUTOMATION')
      .flatMap((i) =>
        i.data.actions
          .filter((a) => a.status === AutomationActionStatus.ERROR)
          .map((a) => ({
            experienceTplId: tpl.id,
            automationTplId: i.data.templateId,
            actionTplId: a.templateId,
            instanceId: d.id
          }))
      );
    const messageRetries = errored
      .filter((i): i is Extract<ItemData, { type: 'MESSAGE' }> => i.type === 'MESSAGE')
      .map((i) =>
        resendExperienceItem({
          orgId: d.data.orgId,
          experienceId: d.id,
          itemId: i.data.templateId,
          itemType: 'MESSAGE',
          actor: i.actor.key,
          channels: i.channels,
          includedTaskStartIds: []
        }).then(() => {
          if (i.data.status === MessageStatus.ERROR) {
            return markMessageResent(d, i.data.templateId);
          }
        })
      );
    try {
      await Promise.all([
        ...(automationRetryArgs.length
          ? [forceRunAutomationActions(d.data.orgId, automationRetryArgs, Date.now())]
          : []),
        ...messageRetries
      ]);
      enqueueSnackbarSuccess(t(RETRY_QUEUED_COPY));
    } catch {
      enqueueSnackbarError(t('Could not retry these items'));
    }
  };

  const doReschedule = (msToMove: number) => {
    setD((x) =>
      moveSelectedExperienceItemsRelatively(tpl, x, selections, msToMove, now())
    );
  };

  // FIXED_DATE items are staged locally like the rest of this panel's edits (no toast,
  // held behind Save). TRIGGERED items have no local representation of "not yet started"
  // to move — force-triggering them is itself the immediate backend action, same as the
  // single-item ForceTriggerButton's onForceTrigger, so those get their own toast.
  const doStartTodayAll = async () => {
    let nextD = d;
    const triggeredItems: ItemData[] = [];
    items.forEach((i, idx) => {
      const eligibility = startTodayEligibilities[idx];
      if (eligibility === 'FIXED_DATE') {
        if (i.type === 'TASK') nextD = moveTaskToToday(tpl, nextD, i.data.id);
        else if (i.type === 'MESSAGE') nextD = moveMessageToToday(tpl, nextD, i.data.templateId);
        else nextD = moveAutomationToToday(tpl, nextD, i.data.templateId);
      } else if (eligibility === 'TRIGGERED') {
        triggeredItems.push(i);
      }
    });
    setD(nextD);
    if (triggeredItems.length) {
      try {
        await Promise.all(
          triggeredItems.map((i) =>
            callFirebaseFunction(CF.experiences.forceTriggerItem, {
              orgId: d.data.orgId,
              tplId: tpl.id,
              instanceId: d.id,
              itemType: i.type === 'MESSAGE' ? 'MSG' : i.type,
              itemTemplateId: i.data.templateId
            })
          )
        );
        enqueueSnackbarSuccess(t('Items triggered successfully!'));
      } catch {
        enqueueSnackbarError(t('Failed to trigger items!'));
      }
    }
  };

  const doResendMulti = async (channelKey: string | 'ALL') => {
    const channelsFor = (i: Extract<ItemData, { type: 'TASK' | 'MESSAGE' }>) =>
      channelKey === 'ALL' ? i.channels : i.channels.filter((c) => channelToKey(c) === channelKey);
    try {
      await Promise.all(
        resendableItems.map((i) =>
          resendExperienceItem(
            i.type === 'TASK'
              ? {
                  orgId: d.data.orgId,
                  experienceId: d.id,
                  itemIds: [i.data.id],
                  itemType: 'TASK',
                  actor: i.actor.key,
                  channels: channelsFor(i)
                }
              : {
                  orgId: d.data.orgId,
                  experienceId: d.id,
                  itemId: i.data.templateId,
                  itemType: 'MESSAGE',
                  actor: i.actor.key,
                  channels: channelsFor(i),
                  includedTaskStartIds: []
                }
          )
        )
      );
      await Promise.all(
        resendableItems
          .filter((i) => i.type === 'MESSAGE' && i.data.status === MessageStatus.ERROR)
          .map((i) => markMessageResent(d, i.data.templateId))
      );
      enqueueSnackbarSuccess(t('Items resent successfully!'));
    } catch {
      enqueueSnackbarError(t('Failed to resend items!'));
    }
  };

  return (
    <PanelScroll>
      <PanelHeaderRow>
        <PanelTitle style={{ marginBottom: 0 }}>
          {t('{{ count }} timeline items selected', { count: items.length })}
        </PanelTitle>
      </PanelHeaderRow>
      <SecondaryButton type="button" onClick={onDeselectAll}>
        <X size={16} /> {t("Deselect all")}
      </SecondaryButton>

      <SectionLabel>{t('Selected timeline items')}</SectionLabel>
      {items.map((i, idx) => {
        const [label, chipStyle] = getItemStatusChipConfig(
          i,
          t,
          taskErrorStates[idx],
          messageErrorStates[idx]
        );
        return (
          <SubtaskRow key={itemKey(i)}>
            <DeselectItemButton
              type="button"
              onClick={() => onDeselectItem(i)}
              aria-label={t('Deselect')}
            >
              <X size={14} />
            </DeselectItemButton>
            <SubtaskLabel>{getItemDisplayName(i.data)}</SubtaskLabel>
            <StatusChip tone={chipStyle.tone} variant={chipStyle.variant}>
              {label}
            </StatusChip>
          </SubtaskRow>
        );
      })}

      {anyDone || anyError || anySkipped || anySkippable ? (
        <>
          <SectionLabel>{t('Manage')}</SectionLabel>
          <ButtonRow>
            {anyDone && (
              <SecondaryButton type="button" onClick={doUncompleteAll}>
                <Undo size={16} /> {t('Uncomplete all tasks')}
              </SecondaryButton>
            )}
            {anyError && (
              <SecondaryButtonWithPromise
                type="button"
                onClick={doRetryAllErrors}
                pending={`${t('Retrying')}...`}
              >
                <RefreshCw size={16} /> {t('Retry all errors')}
              </SecondaryButtonWithPromise>
            )}
            {anySkipped && (
              <SecondaryButton type="button" onClick={doUnskipAll}>
                <Undo size={16} /> {t('Unskip all')}
              </SecondaryButton>
            )}
            {anySkippable && (
              <SecondaryButton type="button" onClick={doSkipAll}>
                <Redo size={16} /> {t('Skip all')}
              </SecondaryButton>
            )}
          </ButtonRow>
        </>
      ) : null}

      {resendableItems.length > 0 && (
        <>
          <SectionLabel>{t('Resend')}</SectionLabel>
          <ButtonRow>
            {uniqBy(
              resendableItems.flatMap((i) => i.channels),
              channelToKey
            ).map((c) => (
              <SecondaryButton
                key={channelToKey(c)}
                type="button"
                onClick={() => doResendMulti(channelToKey(c))}
              >
                <ChannelIcon channel={c} size={16} /> {channelToString(c, t)}
              </SecondaryButton>
            ))}
            <SecondaryButton type="button" onClick={() => doResendMulti('ALL')}>
              <Send size={16} /> {t('All channels')}
            </SecondaryButton>
          </ButtonRow>
          {anyUpcomingResendable && (
            <Hint>
              {t(
                'Upcoming items will not be resent, as they have not yet reached their send date. To send these items today, move them.'
              )}
            </Hint>
          )}
          {resendableItems.some(hasDocusignSubtask) && (
            <Hint>
              {t('Resending in Dado will not resend the Docusign notification. To do this, click')}{' '}
              <strong>{t('Restart signature process')}</strong> {t('instead.')}
            </Hint>
          )}
        </>
      )}

      <SectionLabel>{t('Reschedule')}</SectionLabel>
      {anyStartTodayEligible && (
        <>
          <ButtonRow style={{ marginBottom: tokens.space(2) }}>
            <SecondaryButton type="button" onClick={doStartTodayAll}>
              <Play size={16} /> {t('Start today')}
            </SecondaryButton>
          </ButtonRow>
          <Hint style={{ marginBottom: tokens.space(3) }}>
            {t('Only moves timeline items with the status Upcoming')}
          </Hint>
        </>
      )}
      <RescheduleControl onApply={doReschedule} />
    </PanelScroll>
  );
};

export const TimelineItemDetailsPanel: React.FC<{
  tpl: Doc<IExperienceTemplate>;
  d: Doc<IExperience>;
  setD: React.Dispatch<React.SetStateAction<Doc<IExperience>>>;
  originalD: Doc<IExperience>;
  timelineItems: ItemData[];
  selections: Selections;
  setSelections: React.Dispatch<React.SetStateAction<Selections>>;
  onSave: () => Promise<void>;
  saveInstance: (toSave: Doc<IExperience>) => Promise<void>;
}> = ({
  tpl,
  d,
  setD,
  originalD,
  timelineItems,
  selections,
  setSelections,
  onSave,
  saveInstance
}) => {
  const { t } = useUnsafeTranslation();

  const selectedItems = useMemo(
    () =>
      timelineItems.filter((i) => {
        if (i.type === 'TASK') return !!selections.tasks[i.data.templateId];
        if (i.type === 'MESSAGE') return !!selections.messages[i.data.templateId];
        return !!selections.automations[i.data.templateId];
      }),
    [timelineItems, selections]
  );

  const isDirty = !isEqual(d, originalD);

  return (
    <Panel>
      {selectedItems.length === 0 && (
        <Placeholder>
          <List />
          {t('Select a timeline item/items to see details and make changes')}
        </Placeholder>
      )}
      {selectedItems.length === 1 && (
        <SingleItemDetailsPanel
          tpl={tpl}
          d={d}
          setD={setD}
          originalD={originalD}
          item={selectedItems[0]}
          setSelections={setSelections}
          saveInstance={saveInstance}
        />
      )}
      {selectedItems.length > 1 && (
        <MultiItemDetailsPanel
          tpl={tpl}
          d={d}
          setD={setD}
          items={selectedItems}
          onDeselectAll={() =>
            setSelections({ tasks: {}, messages: {}, automations: {} })
          }
          onDeselectItem={(item) =>
            setSelections((s) => {
              if (item.type === 'TASK') {
                return { ...s, tasks: { ...s.tasks, [item.data.templateId]: false } };
              }
              if (item.type === 'MESSAGE') {
                return { ...s, messages: { ...s.messages, [item.data.templateId]: false } };
              }
              return { ...s, automations: { ...s.automations, [item.data.templateId]: false } };
            })
          }
        />
      )}
      {selectedItems.length > 0 && (
        <SaveDiscardBar
          isDirty={isDirty}
          onSave={onSave}
          onDiscard={() => setD(originalD)}
        />
      )}
    </Panel>
  );
};
